import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Icono } from "./iconos";
import { defaultFilters, fetchCampaignOptions, fetchDashboard } from "./dashboardApi";
import {
    ZMLayout,
    ZMCard,
    ZMChartBox,
    ZMMetricCard,
    ZMTable,
    ZMBadge,
    ZMFiltersBar,
    ZMEmptyState,
    ZMLoadingState,
    ZMErrorState,
    buildZmChartOptions,
} from "./components/ZMUI";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const fmt = (n, dec = 0) =>
    n == null ? "0" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtUsd = (n) => `US$ ${fmt(n || 0, 2)}`;
const fmtPct = (n) => `${fmt(n || 0, 2)}%`;

export default function ZMCampanas() {
    const { darkMode } = useContext(DarkModeContext);
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState("");
    const [filters, setFilters] = useState(() => defaultFilters());
    const [draftFilters, setDraftFilters] = useState(() => defaultFilters());
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [datos, setDatos] = useState({
        kpis: {},
        campanias: [],
        campaigns: [],
        dailyPerformance: [],
        ranking: [],
        rankingPorCpl: [],
        alertas: [],
        campaignOptions: [],
        meta: {},
    });

    const cargar = async (nextFilters = filters) => {
        const token = getToken();
        if (!token) {
            navigate("/");
            return;
        }

        setCargando(true);
        setError("");
        try {
            const [payload, optionItems] = await Promise.all([
                fetchDashboard("campanaszt", token, nextFilters),
                fetchCampaignOptions(token, nextFilters),
            ]);
            setDatos(payload || {});
            setCampaignOptions(optionItems);
        } catch (e) {
            if (e?.code === 401 || e?.code === 403 || e?.message === "unauthorized") {
                navigate("/");
                return;
            }
            setError("No se pudieron cargar campañas de Meta.");
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargar(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const rows = useMemo(() => datos.campanias || datos.campaigns || [], [datos]);
    const kpis = datos.kpis || {};
    const options = campaignOptions;
    const alerts = datos.alertas || [];
    const daily = useMemo(() => datos.dailyPerformance || [], [datos]);
    const chartOptions = useMemo(() => buildZmChartOptions({
        leftTitle: "Leads CRM",
        rightTitle: "Inversion (US$)",
        showRightAxis: true,
        darkMode,
    }), [darkMode]);

    const chartData = useMemo(() => ({
        labels: daily.map((x) => x.fecha),
        datasets: [
            {
                type: "bar",
                label: "Leads CRM",
                data: daily.map((x) => x.leads || 0),
                backgroundColor: "rgba(180, 211, 47, 0.78)",
                borderColor: "rgba(154, 197, 0, 0.95)",
                borderWidth: 1,
                borderRadius: 10,
                maxBarThickness: 34,
                yAxisID: "y",
            },
            {
                type: "line",
                label: "Inversión",
                data: daily.map((x) => x.inversion || 0),
                borderColor: "#1f2937",
                backgroundColor: "rgba(31, 41, 55, 0.12)",
                tension: 0.38,
                borderWidth: 3,
                fill: false,
                pointRadius: 2.5,
                pointHoverRadius: 5,
                pointBackgroundColor: "#1f2937",
                yAxisID: "y1",
            },
        ],
    }), [daily]);

    if (cargando) return <ZMLoadingState label="Cargando campañas..." fullScreen />;
    if (error) return <ZMErrorState message={error} onRetry={() => cargar(filters)} />;

    return (
        <ZMLayout
            title="Rendimiento de campañas"
            subtitle="Lectura consolidada de inversión Meta, leads CRM y diferencia de atribución por campaña."
            right={datos?.meta?.mensaje || "Actualizado"}
            className={darkMode ? "container-dark" : ""}
        >
            <ZMFiltersBar className="mb-3">
                <div>
                    <label>Desde</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaDesde || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaDesde: e.target.value }))} />
                </div>
                <div>
                    <label>Hasta</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaHasta || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaHasta: e.target.value }))} />
                </div>
                <div style={{ minWidth: 260 }}>
                    <label>Campaña</label>
                    <select className="form-select form-select-sm" value={draftFilters.campania || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, campania: e.target.value }))}>
                        <option value="">Todas las campañas</option>
                        {options.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
                    </select>
                </div>
                <div>
                    <label>Estado</label>
                    <select className="form-select form-select-sm" value={draftFilters.estado || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, estado: e.target.value }))}>
                        <option value="">Todos</option>
                        <option value="ACTIVE">Activa</option>
                        <option value="PAUSED">Pausada</option>
                        <option value="ARCHIVED">Archivada</option>
                        <option value="DELETED">Eliminada</option>
                    </select>
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-sm zm-btn-primary" onClick={() => {
                        setFilters({ ...draftFilters });
                        cargar({ ...draftFilters });
                    }}>
                        Aplicar
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => {
                        const reset = defaultFilters();
                        setDraftFilters(reset);
                        setFilters(reset);
                        cargar(reset);
                    }}>
                        Limpiar
                    </button>
                </div>
            </ZMFiltersBar>

            <div className="zm-grid-metrics">
                <ZMMetricCard icon={<Icono.Users />} label="Leads CRM" value={fmt(kpis.leadsCrm ?? kpis.leads, 0)} helper="Fuente principal del panel" />
                <ZMMetricCard icon={<Icono.BarChart />} label="Leads reportados por Meta" value={fmt(kpis.leadsMetaReported, 0)} helper="Actions de Meta Ads" />
                <ZMMetricCard icon={<Icono.AlertTriangle />} label="Diferencia" value={fmt(kpis.leadsDiferencia, 0)} helper="Meta reportados - Leads CRM" />
                <ZMMetricCard icon={<Icono.Wallet />} label="Inversión" value={fmtUsd(kpis.inversion)} />
                <ZMMetricCard icon={<Icono.Dollar />} label="CPL CRM" value={fmtUsd(kpis.cpl)} />
                <ZMMetricCard icon={<Icono.MousePointer />} label="CTR promedio" value={fmtPct(kpis.ctr)} />
                <ZMMetricCard icon={<Icono.Phone />} label="Clicks" value={fmt(kpis.clicks, 0)} />
                <ZMMetricCard icon={<Icono.Eye />} label="Impresiones" value={fmt(kpis.impresiones, 0)} />
            </div>

            <ZMCard className="mb-3" bodyClassName="py-2">
                <div className="zm-inline-note">
                    <strong>Regla del panel:</strong> campaña = <b>inversión y delivery desde Meta</b> + <b>leads reales desde CRM</b>. Si Meta reporta más leads que LeadsMeta, lo mostramos como diferencia en vez de mezclarlo con el KPI comercial.
                </div>
            </ZMCard>

            {rows.length === 0 ? (
                <ZMEmptyState title="Sin campañas visibles" hint="No hubo resultados con los filtros actuales o Meta no devolvió datos para el período." />
            ) : (
                <>
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-xl-8">
                            <ZMCard title="Inversión y leads por día" subtitle="Cruce de gasto Meta con leads reales del CRM" className="h-100">
                                {daily.length > 0 ? (
                                    <ZMChartBox>
                                        <Bar data={chartData} options={chartOptions} />
                                    </ZMChartBox>
                                ) : <ZMEmptyState title="Sin serie diaria" hint="No se registraron movimientos en el rango actual." />}
                            </ZMCard>
                        </div>
                        <div className="col-12 col-md-6 col-xl-2">
                            <ZMCard title="Ranking por leads CRM" className="h-100">
                                <div className="zm-stat-list">
                                    {(datos.ranking || []).map((item, index) => (
                                        <div key={`${item.campaignId}-${index}`} className="zm-stat-item">
                                            <div>
                                                <strong>{index + 1}. {item.campania}</strong>
                                                <span>{fmtUsd(item.cpl)} CPL CRM</span>
                                            </div>
                                            <b>{fmt(item.leads, 0)}</b>
                                        </div>
                                    ))}
                                </div>
                            </ZMCard>
                        </div>
                        <div className="col-12 col-md-6 col-xl-2">
                            <ZMCard title="Ranking por CPL CRM" className="h-100">
                                <div className="zm-stat-list">
                                    {(datos.rankingPorCpl || []).map((item, index) => (
                                        <div key={`${item.campaignId}-cpl-${index}`} className="zm-stat-item">
                                            <div>
                                                <strong>{index + 1}. {item.campania}</strong>
                                                <span>{fmt(item.leads, 0)} leads CRM</span>
                                            </div>
                                            <b>{fmtUsd(item.cpl)}</b>
                                        </div>
                                    ))}
                                </div>
                            </ZMCard>
                        </div>
                    </div>

                    <ZMCard title="Comparación de campañas" subtitle="Meta Ads + leads CRM + diferencia de atribución" className="mb-3">
                        <ZMTable
                            emptyText="Sin campañas para comparar."
                            columns={[
                                { key: "campania", header: "Campaña", render: (r) => <div><strong>{r.campania}</strong><div className="text-muted small">{r.campaignId}</div></div> },
                                { key: "estado", header: "Estado", render: (r) => <ZMBadge status={r.estado} /> },
                                { key: "objetivo", header: "Objetivo", render: (r) => r.objetivo || "Sin objetivo" },
                                { key: "leadsCrm", header: "Leads CRM", cellClassName: "text-end", render: (r) => fmt(r.leadsCrm ?? r.leads, 0) },
                                { key: "leadsMetaReported", header: "Meta reportados", cellClassName: "text-end", render: (r) => fmt(r.leadsMetaReported, 0) },
                                { key: "leadsDiferencia", header: "Diferencia", cellClassName: "text-end", render: (r) => fmt(r.leadsDiferencia, 0) },
                                { key: "inversion", header: "Inversión", cellClassName: "text-end", render: (r) => fmtUsd(r.inversion) },
                                { key: "cpl", header: "CPL CRM", cellClassName: "text-end", render: (r) => fmtUsd(r.cpl) },
                                { key: "impresiones", header: "Impresiones", cellClassName: "text-end", render: (r) => fmt(r.impresiones, 0) },
                                { key: "alcance", header: "Alcance", cellClassName: "text-end", render: (r) => fmt(r.alcance, 0) },
                                { key: "clicks", header: "Clicks", cellClassName: "text-end", render: (r) => fmt(r.clicks, 0) },
                                { key: "ctr", header: "CTR", cellClassName: "text-end", render: (r) => fmtPct(r.ctr) },
                                { key: "cpm", header: "CPM", cellClassName: "text-end", render: (r) => fmtUsd(r.cpm) },
                            ]}
                            rows={rows}
                        />
                    </ZMCard>

                    <div className="row g-3">
                        <div className="col-12 col-lg-7">
                            <ZMCard title="Leads CRM por campaña" subtitle="Ordenado por volumen real guardado en LeadsMeta">
                                <ZMTable
                                    emptyText="Sin ranking por leads."
                                    columns={[
                                        { key: "campania", header: "Campaña" },
                                        { key: "leads", header: "Leads CRM", cellClassName: "text-end", render: (r) => fmt(r.leads, 0) },
                                        { key: "cpl", header: "CPL CRM", cellClassName: "text-end", render: (r) => fmtUsd(r.cpl) },
                                        { key: "inversion", header: "Inversión", cellClassName: "text-end", render: (r) => fmtUsd(r.inversion) },
                                    ]}
                                    rows={(datos.ranking || []).slice(0, 5)}
                                />
                            </ZMCard>
                        </div>
                        <div className="col-12 col-lg-5">
                            <ZMCard title="Alertas" subtitle="Desvíos y oportunidades detectadas">
                                {alerts.length === 0 ? (
                                    <ZMEmptyState title="Sin alertas activas" hint="Las campañas no muestran desvíos relevantes en este período." />
                                ) : (
                                    <div className="zm-alert-stack">
                                        {alerts.map((alerta, index) => (
                                            <div key={`${alerta.tipo}-${index}`} className={`zm-alert-item ${alerta.tipo || ""}`}>
                                                <div className="d-flex justify-content-between align-items-start gap-2">
                                                    <div>
                                                        <strong>{alerta.campania || "Campaña"}</strong>
                                                        <p>{alerta.mensaje}</p>
                                                    </div>
                                                    <ZMBadge
                                                        status={
                                                            alerta.tipo === "ganadora" ? "cerrado"
                                                                : alerta.tipo === "cpl_alto" ? "contactado"
                                                                    : "rechazado"
                                                        }
                                                        label={String(alerta.tipo || "alerta").replaceAll("_", " ")}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </ZMCard>
                        </div>
                    </div>
                </>
            )}
        </ZMLayout>
    );
}
